import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import RouteProgressBar from "@/components/RouteProgressBar";
import { WelcomeModal } from "@/components/WelcomeModal";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
// useBetaAccess removed - was causing timeouts
import { AdminModeProvider } from "@/contexts/AdminModeContext";
import { BadgeNotificationProvider } from "@/components/BadgeNotificationProvider";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { PerformanceMonitor } from "@/components/PerformanceMonitor";
import { NetworkStatusIndicator } from "@/components/NetworkStatusIndicator";
import ScrollToTop from "@/components/ScrollToTop";
// SignInModal removed - auth now redirects to /auth page
import { WaitlistGuard } from "@/components/WaitlistGuard";
import { MobileChatProvider } from "@/contexts/MobileChatContext";
import { supabase } from "@/integrations/supabase/client";

/*
  Route-level code splitting.

  Every page used to be a static import, so Rollup emitted one 1.65 MB entry
  chunk (415 KB gzipped)   containing the admin console, the chat app, the deck,
  and the ROI calculator — all of it parsed before the
  marketing homepage could render a single pixel.

  MerchantsSimple and NotFound stay eager: MerchantsSimple *is* "/" (and the
  eight Shopify SEO routes), so lazy-loading it would only add a round trip in
  front of the most important page on the site, and NotFound is tiny and is the
  fallback for everything.
*/
import MerchantsSimple from "./pages/MerchantsSimple";
import NotFound from "./pages/NotFound";

const Landing = lazy(() => import("./pages/Landing"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Careers = lazy(() => import("./pages/Careers"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AffiliateDisclosure = lazy(() => import("./pages/AffiliateDisclosure"));
const LegalHub = lazy(() => import("./pages/LegalHub"));
const TrendsAdmin = lazy(() => import("./pages/TrendsAdmin"));
const AuthNew = lazy(() => import("./pages/AuthNew"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Look = lazy(() => import("./pages/Look"));
const StyleChat = lazy(() => import("./pages/StyleChat"));
const Loading = lazy(() => import("./pages/Loading"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const ExtensionPreview = lazy(() => import("./pages/ExtensionPreview"));
const ExtensionSettings = lazy(() => import("./pages/ExtensionSettings"));
const EmailConfirmation = lazy(() => import("./pages/EmailConfirmation"));
const EmailConfirmed = lazy(() => import("./pages/EmailConfirmed"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const WaitlistAdmin = lazy(() => import("./pages/WaitlistAdmin"));
const Docs = lazy(() => import("./pages/Docs"));
const ChromeExtension = lazy(() => import("./pages/ChromeExtension"));
const CelebrityOutfitsAdmin = lazy(() => import("./pages/CelebrityOutfitsAdmin"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Faq = lazy(() => import("./pages/Faq"));
const Articles = lazy(() => import("./pages/Articles"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const Billing = lazy(() => import("./pages/Billing"));
const CheckMyAdminStatus = lazy(() => import("./pages/CheckMyAdminStatus"));
const Onboard = lazy(() => import("./pages/Onboard"));
const SharedLook = lazy(() => import("./pages/SharedLook"));
const Merchants = lazy(() => import("./pages/Merchants"));
const DemoStore = lazy(() => import("./pages/DemoStore"));

/* demo.garu.ai serves ONLY the demo store, at the domain root — the
   storefront is the whole site there, so a visitor never sees the
   /demo-store prefix or wanders into marketing pages. `demo.localhost`
   is included so the host behaviour is testable in local dev. PDPs stay
   under /products/ because the real widget's isProductPage() check
   requires that path segment (the Shopify storefront convention). */
const IS_DEMO_HOST =
  typeof window !== "undefined" &&
  /^demo\.(garu\.ai|localhost)$/.test(window.location.hostname);

/* Old /demo-store URLs hop to the vanity domain, deep paths preserved
   (/demo-store/products/x → demo.garu.ai/products/x). */
const DemoStoreHop = () => {
  React.useEffect(() => {
    const rest = window.location.pathname.replace(/^\/demo-store/, "") || "/";
    window.location.replace(`https://demo.garu.ai${rest}`);
  }, []);
  return null;
};
const Pitch = lazy(() => import("./pages/Pitch"));
const Deck = lazy(() => import("./pages/Deck"));
const RoiCalculator = lazy(() => import("./pages/RoiCalculator"));
const Brand = lazy(() => import("./pages/Brand"));

/** /articles/:slug moved to /blog/:slug — preserve deep links. */
function ArticleToBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug}`} replace />;
}

// Configure React Query with aggressive caching for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // Keep unused data in cache for 24 hours (was cacheTime)
      retry: 2,
      refetchOnWindowFocus: false, // Don't refetch when user returns to tab
      refetchOnMount: false, // Don't refetch on component mount if data exists
      refetchOnReconnect: false, // Don't refetch on reconnect
    },
  },
});

// Feature flags
const ENABLE_WELCOME_MODAL = import.meta.env.VITE_ENABLE_WELCOME_MODAL === "true";

/**
 * Is there a Supabase session persisted in this browser?
 *
 * supabase-js keeps it under `sb-<project-ref>-auth-token` in localStorage.
 * Reading that key is synchronous, so we can tell "signed-out visitor" from
 * "signed-in user we are about to redirect" during the very first render —
 * before `getSession()` has resolved.
 */
function hasPersistedSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && /^sb-.*-auth-token$/.test(key)) return true;
    }
  } catch {
    // Private mode / storage disabled — treat as signed out and render the
    // marketing page, which is the correct view for an anonymous visitor.
  }
  return false;
}

// Simple homepage routing - logged in = closet, logged out = Merchants (B2B)
const HomeRoute = () => {
  const { user, loading } = useAuth();

  // Only gate on auth for visitors who actually have a stored session and are
  // therefore heading to /closet. Blocking *every* visitor on getSession()
  // meant the marketing homepage — the page almost all traffic lands on, and
  // the one crawlers see — rendered nothing but "Loading..." until Supabase
  // had booted and answered.
  if (loading && hasPersistedSession()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-foreground/50">Loading...</div>
      </div>
    );
  }

  // Logged out → Merchants page (B2B focus)
  if (!user) {
    return <MerchantsSimple />;
  }

  // Logged in → Main app
  return <Navigate to="/closet" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <AdminModeProvider>
          <MobileChatProvider>
            <BadgeNotificationProvider>
              <Toaster />
              <Sonner />
              {ENABLE_WELCOME_MODAL ? <WelcomeModal /> : null}
              <NetworkStatusIndicator />
              <BrowserRouter future={{ v7_relativeSplatPath: true }}>
            <WaitlistGuard>
            <RouteProgressBar />
            <ScrollToTop />
            {/* Fallback for a route chunk that genuinely has not arrived yet.
                It is deliberately minimal — "/" renders eagerly, so this never
                covers the first paint of the landing page. */}
            <Suspense
              fallback={
                <div
                  className="min-h-screen flex items-center justify-center bg-background"
                  role="status"
                  aria-live="polite"
                >
                  <span className="sr-only">Loading page…</span>
                </div>
              }
            >
            {IS_DEMO_HOST ? (
              <Routes>
                <Route path="/" element={<DemoStore />} />
                <Route path="/products/:handle" element={<DemoStore />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : (
            <Routes>
          {/* Core User Pages */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/shoppers" element={<Landing />} />
          <Route path="/landing" element={<Navigate to="/shoppers" replace />} />
          <Route path="/about" element={<About />} />
          {/* Explore + celebrity browse parked in /future_features.
              Keep these redirects so old bookmarks and shared links
              do not 404. */}
          <Route path="/explore" element={<Navigate to="/" replace />} />
          <Route path="/celebrity/:slug" element={<Navigate to="/" replace />} />
          <Route path="/home" element={<HomeRoute />} />
          <Route path="/auth" element={<AuthNew />} />
          <Route path="/onboard" element={<Onboard />} />
          <Route path="/closet" element={<Favorites />} />
          <Route path="/favorites" element={<Navigate to="/closet" replace />} />
          <Route path="/chat" element={<StyleChat />} />
          <Route path="/look/:id" element={<Look />} />
          <Route path="/shared-look/:id" element={<SharedLook />} />
          <Route path="/profile-settings" element={<ProfileSettings />} />
          <Route path="/profile/:username" element={<Profile />} />
          
          {/* Monetization Pages */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/billing" element={<Billing />} />
          
          {/* Legal/Info Pages */}
          <Route path="/legal" element={<LegalHub />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/blog" element={<Articles />} />
          <Route path="/blog/:slug" element={<ArticlePage />} />
          <Route path="/compare/:slug" element={<ComparePage />} />
          <Route path="/articles" element={<Navigate to="/blog" replace />} />
          <Route path="/articles/:slug" element={<ArticleToBlogRedirect />} />
          
          {/* Admin pages trimmed in simplified build */}
          
          {/* Auth Flow Pages */}
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/email-confirmation" element={<EmailConfirmation />} />
          <Route path="/email-confirmed" element={<EmailConfirmed />} />
          
          {/* PAUSED FEATURES - Coming in Future Versions */}
          {/* Chat and experimental features moved to /future_features */}
          <Route path="/docs" element={<Docs />} />
          <Route path="/chrome-extension" element={<ChromeExtension />} />
          <Route path="/virtual-try-on-chrome-extension" element={<ChromeExtension />} />
          <Route path="/careers" element={<Careers />} />
          {/* /how-it-works as a standalone page was retired — the
              footer's Resources > "How it works" link now anchors
              to the in-page #how-it-works section on the landing
              route, where the 4-step narrative sits next to the
              embedded Virtual Try-On demo video. The reusable
              <HowItWorks /> *component* is still imported by /about
              from components/HowItWorks.tsx. */}
          {/* Affiliate shop moved to future_features */}
          <Route path="/celebrity-outfits-admin" element={<CelebrityOutfitsAdmin />} />
          
          {/* B2B MERCHANT PAGES */}
          <Route path="/merchants" element={<MerchantsSimple />} />
          <Route path="/merchants/ai-sizing" element={<MerchantsSimple />} />
          {/* The demo store lives ONLY at demo.garu.ai now. Server-side
              visits to the old /demo-store URLs are 307'd by vercel.json;
              this splat covers client-side navigations and anything that
              hydrates before the edge redirect. Local dev tests the store
              at demo.localhost:<port> (see IS_DEMO_HOST). */}
          <Route path="/demo-store/*" element={<DemoStoreHop />} />
          <Route path="/roi-calculator" element={<RoiCalculator />} />
          <Route path="/shopify-ai-sizing" element={<MerchantsSimple />} />
          <Route path="/shopify-size-finder" element={<MerchantsSimple />} />
          <Route path="/shopify-virtual-try-on" element={<MerchantsSimple />} />
          <Route path="/shopify-virtual-try-on-app" element={<MerchantsSimple />} />
          <Route path="/virtual-fitting-room-shopify" element={<MerchantsSimple />} />
          <Route path="/reduce-fashion-returns" element={<MerchantsSimple />} />
          <Route path="/ai-fashion-models" element={<MerchantsSimple />} />
          <Route path="/ai-product-photography" element={<MerchantsSimple />} />
          <Route path="/merchants/full" element={<Merchants />} />
          <Route path="/pitch" element={<Pitch />} />
          <Route path="/deck" element={<Deck />} />
          <Route path="/brand" element={<Brand />} />
          <Route path="/brand-kit" element={<Navigate to="/brand" replace />} />
          <Route path="/press" element={<Navigate to="/brand" replace />} />
          {/* /playbook moved to its own private Vercel app — see apps/playbook. Hits on garu.ai/playbook are redirected to /404. */}

          {/* HIDDEN ADMIN/TEST PAGES */}
          <Route path="/trends-admin" element={<TrendsAdmin />} />
          <Route path="/admin-users" element={<AdminUsers />} />
          <Route path="/user-directory" element={<WaitlistAdmin />} />
          <Route path="/check-admin-status" element={<CheckMyAdminStatus />} />
          <Route path="/loading" element={<Loading />} />
          <Route path="/extension-preview" element={<ExtensionPreview />} />
          <Route path="/extension-settings" element={<ExtensionSettings />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
          </Routes>
            )}
            </Suspense>
            </WaitlistGuard>
          </BrowserRouter>
          <VercelAnalytics />
          {/* <PerformanceMonitor /> */}
            </BadgeNotificationProvider>
          </MobileChatProvider>
        </AdminModeProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
// Force deployment Thu Oct  9 05:59:12 AM UTC 2025
